import React, { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { markSettlementPaid, markSettlementFailed } from "@admin/services/adminApi";
import { paise, num } from "@admin/components/analytics/AnalyticsPrimitives";

const todayIST = () => new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });

function Summary({ s }) {
  return (
    <div className="rounded-xl bg-slate-50 border border-slate-200 p-3 text-xs text-slate-600 grid grid-cols-2 gap-x-6 gap-y-1" data-testid="settlement-action-summary">
      <span>Lawyer: <strong className="text-[#0A2540]">{s.lawyer_name}</strong></span>
      <span>Amount: <strong className="text-[#0A2540]" data-testid="action-amount">{paise(s.amount_paise)}</strong></span>
      <span>Earnings: <strong className="text-[#0A2540]">{num(s.earning_consultation_ids?.length || 0)}</strong></span>
      <span>Period: <strong className="text-[#0A2540]">{s.settlement_period?.label || "—"}</strong></span>
    </div>
  );
}

export function SettlementActions({ settlement: s, onDone }) {
  const [mode, setMode] = useState(null);
  const [utr, setUtr] = useState("");
  const [date, setDate] = useState(todayIST());
  const [notes, setNotes] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  if (!["approved", "processing"].includes(s.status)) return null;

  const run = async (fn, okMsg) => {
    setBusy(true);
    try {
      const p = await fn();
      toast.success(okMsg);
      setMode(null);
      onDone(p);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
      onDone(null);
    } finally {
      setBusy(false);
    }
  };

  if (!mode) {
    return (
      <div className="mt-4 flex flex-wrap gap-2" data-testid="settlement-actions">
        <Button onClick={() => setMode("paid")} className="bg-emerald-700 hover:bg-emerald-800" data-testid="action-mark-paid">Mark Paid</Button>
        <Button variant="outline" onClick={() => setMode("failed")} className="text-rose-700 border-rose-200 hover:bg-rose-50" data-testid="action-transfer-failed">Transfer Failed</Button>
      </div>
    );
  }

  if (mode === "paid") {
    return (
      <div className="mt-4 space-y-3" data-testid="mark-paid-form">
        <p className="text-sm font-bold text-[#0A2540]">Confirm bank transfer</p>
        <Summary s={s} />
        <Input placeholder="UTR / bank transfer reference" value={utr} onChange={(e) => setUtr(e.target.value)} data-testid="mark-paid-utr" />
        <Input type="date" value={date} max={todayIST()} onChange={(e) => setDate(e.target.value)} data-testid="mark-paid-date" />
        <Input placeholder="Admin note (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="mark-paid-notes" />
        <div className="flex gap-2 justify-end">
          <Button variant="outline" onClick={() => setMode(null)} disabled={busy} data-testid="mark-paid-cancel">Cancel</Button>
          <Button disabled={busy || utr.trim().length < 6 || !date} className="bg-emerald-700 hover:bg-emerald-800" data-testid="mark-paid-confirm"
            onClick={() => run(() => markSettlementPaid(s.payout_id, { utr: utr.trim(), transferred_at: date, notes: notes.trim() || undefined }), "Settlement marked paid")}>
            {busy ? "Saving…" : `Mark ${paise(s.amount_paise)} Paid`}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-4 space-y-3" data-testid="transfer-failed-form">
      <p className="text-sm font-bold text-rose-700">Record failed transfer</p>
      <Summary s={s} />
      <Input placeholder="Failure reason (required)" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="transfer-failed-reason" />
      <p className="text-xs text-slate-500">The attributed earnings are released and will reappear in the payout queue.</p>
      <div className="flex gap-2 justify-end">
        <Button variant="outline" onClick={() => setMode(null)} disabled={busy} data-testid="transfer-failed-cancel">Cancel</Button>
        <Button disabled={busy || reason.trim().length < 3} className="bg-rose-700 hover:bg-rose-800" data-testid="transfer-failed-confirm"
          onClick={() => run(() => markSettlementFailed(s.payout_id, { reason: reason.trim() }), "Settlement marked failed — earnings released")}>
          {busy ? "Saving…" : "Confirm Transfer Failed"}
        </Button>
      </div>
    </div>
  );
}
